import React, { Suspense } from 'react';
import { IntlProvider } from 'react-intl';
import { QueryClientProvider } from 'react-query';
import { ActiveSessionProviderV2 } from '@onramp/components/ActiveSessionProviderV2';
import { ApplePayProvider } from '@onramp/components/ApplePayProvider';
import { AppManagerProvider } from '@onramp/components/AppManagerProvider';
import type { WidgetBackgroundVariant } from '@onramp/components/AppThemeProvider';
import { AppThemeProvider } from '@onramp/components/AppThemeProvider';
import ErrorFrameworkProvider from '@onramp/components/error/ErrorFrameworkProvider';
import { ExperimentationProvider } from '@onramp/components/ExperimentationProvider';
import { GraphqlProvider } from '@onramp/components/GraphqlProvider';
import { IsInitialScreenProvider } from '@onramp/components/InitialScreenProvider';
import { JotaiRelayProvider } from '@onramp/components/JotaiRelayProvider';
import KillSwitchGuard from '@onramp/components/KillSwitchGuard';
import { PolicyRestrictionsGuard } from '@onramp/components/PolicyRestrictionsGuard';
import { RiskifiedBeacon } from '@onramp/components/RiskifiedBeacon';
import { RiskSessionManagerProvider } from '@onramp/components/RiskSessionManagerProvider';
import { GenericLoadingScreen } from '@onramp/components/screens/GenericLoadingScreen';
import SplashScreen from '@onramp/components/SplashScreen/SplashScreen';
import { useEffectOnMount } from '@onramp/hooks/useEffectOnMount';
import { BuyWidgetStateProvider } from '@onramp/state/BuyWidgetState';
import { OnrampNetworksProvider } from '@onramp/state/contexts/OnrampNetworksProvider';
import { PageDataProvider } from '@onramp/state/PageData/PageDataProvider';
import type { InitParamsType, InternalInitParamsType } from '@onramp/types/initParams';
import { useIsGuestCheckoutPath } from '@onramp/utils/guestCheckoutUtils';
import { prefetchRestQueries, queryClient } from '@onramp/utils/reactQueryClient';
import { useThemeSpectrum } from '@onramp/utils/useThemeSpectrum';
import { InitParamsProvider } from '@onramp/v2/client/contexts/initParams/InitParamsContext';
import type { Variants } from 'framer-motion';
import { AnimatePresence, motion } from 'framer-motion';
import { styled } from 'linaria/react';
import { useRouter } from 'next/router';
import { PortalProvider } from '@cbhq/cds-web/overlays/PortalProvider';
import type { MessagesMap } from '@cbhq/intl';
type I18nProviderProps = {
  locale: string;
  messages: MessagesMap;
};

/**
 * Minimal provider for content that that doesn't require other providers like CDS i.e. button, oauth redirect.
 */
export const MinimalContentProvider: React.FC<I18nProviderProps & JSX.IntrinsicElements['div']> = ({
  children,
  id,
  locale,
  messages
}) => {
  const spectrum = useThemeSpectrum();
  const DEFAULT_LOCALE = useRouter().locale;
  return <QueryClientProvider client={queryClient}>
      <AppThemeProvider spectrum={spectrum} widgetBackgroundVariant="solid">
        <IntlProvider locale={locale} defaultLocale={DEFAULT_LOCALE} messages={messages}>
          <div id={id}>{children}</div>
        </IntlProvider>
      </AppThemeProvider>
    </QueryClientProvider>;
};
export const FlowAgnosticProvider = ({
  children,
  locale,
  messages
}: I18nProviderProps & {
  children: React.ReactNode;
}) => {
  return <MinimalContentProvider locale={locale} messages={messages}>
      <ErrorFrameworkProvider level="screen">{children}</ErrorFrameworkProvider>
    </MinimalContentProvider>;
};

/**
 * Shared common widget provider
 */
type WidgetProviderProps = I18nProviderProps & {
  initParamsFromServer: InitParamsType | undefined;
  internalInitParamsFromServer: InternalInitParamsType | undefined;
  // Widget specific content providers that are required for widget specific functionality i.e. buy state
  WidgetContentProvider?: React.FunctionComponent;
  widgetBackgroundVariant?: WidgetBackgroundVariant;
};
const PassthroughProvider: React.FC = ({
  children
}) => <>{children}</>;
const emptyInitParams = {
  appId: '',
  destinationWallets: []
};
export const WidgetProvider: React.FC<WidgetProviderProps> = ({
  WidgetContentProvider = PassthroughProvider,
  widgetBackgroundVariant,
  children,
  initParamsFromServer,
  internalInitParamsFromServer,
  locale,
  messages
}) => {
  const router = useRouter();
  const DEFAULT_LOCALE = router.locale;
  const spectrum = useThemeSpectrum();
  const isGuestCheckoutPath = useIsGuestCheckoutPath();
  useEffectOnMount(prefetchRestQueries);
  const skipAuthProviders = isGuestCheckoutPath;

  // Some providers should be skipped for Guest Checkout
  const WidgetActiveSessionProvider = skipAuthProviders ? PassthroughProvider : ActiveSessionProviderV2;
  const WidgetApplePayProvider = skipAuthProviders ? PassthroughProvider : ApplePayProvider;
  const WidgetPolicyRestrictionsGuard = skipAuthProviders ? PassthroughProvider : PolicyRestrictionsGuard;
  return <QueryClientProvider client={queryClient}>
      <AppThemeProvider spectrum={spectrum} widgetBackgroundVariant={widgetBackgroundVariant}>
        <IntlProvider locale={locale} defaultLocale={DEFAULT_LOCALE} messages={messages}>
          <IsInitialScreenProvider>
            <ErrorFrameworkProvider level="root">
              <ExperimentationProvider>
                <GraphqlProvider>
                  <JotaiRelayProvider>
                    <InitParamsProvider initParamsFromServer={initParamsFromServer ?? emptyInitParams} internalInitParamsFromServer={internalInitParamsFromServer}>
                      <SplashScreen>
                        <AppManagerProvider>
                          <WidgetActiveSessionProvider>
                            <PortalProvider>
                              <Suspense fallback={<GenericLoadingScreen />}>
                                <ErrorFrameworkProvider level="application">
                                  <PageDataProvider>
                                    <WidgetContentProvider>
                                      <WidgetApplePayProvider>
                                        <WidgetPolicyRestrictionsGuard>
                                          <ErrorFrameworkProvider level="screen">
                                            <AnimatePresence mode="wait">
                                              <Main key={router.route} variants={VARIANTS} initial={false} animate="visible" exit="hidden">
                                                <KillSwitchGuard>{children}</KillSwitchGuard>
                                              </Main>
                                            </AnimatePresence>
                                          </ErrorFrameworkProvider>
                                        </WidgetPolicyRestrictionsGuard>
                                      </WidgetApplePayProvider>
                                    </WidgetContentProvider>
                                  </PageDataProvider>
                                </ErrorFrameworkProvider>
                              </Suspense>
                            </PortalProvider>
                          </WidgetActiveSessionProvider>
                        </AppManagerProvider>
                      </SplashScreen>
                    </InitParamsProvider>
                  </JotaiRelayProvider>
                </GraphqlProvider>
              </ExperimentationProvider>
            </ErrorFrameworkProvider>
          </IsInitialScreenProvider>
        </IntlProvider>
      </AppThemeProvider>
    </QueryClientProvider>;
};
const Main = /*#__PURE__*/styled(motion.main)({
  name: "Main",
  class: "m1sizj4y"
});
const VARIANTS: Variants = {
  hidden: {
    opacity: 0,
    transition: {
      duration: 0.15,
      ease: 'easeOut'
    }
  },
  visible: {
    opacity: 1,
    transition: {
      duration: 0.15,
      ease: 'easeIn'
    }
  }
};

/**
 * Widget specific providers
 */
const BuyWidgetContentProvider: React.FC = ({
  children
}) => {
  const isGuestCheckoutPath = useIsGuestCheckoutPath();
  return <BuyWidgetStateProvider>
      {isGuestCheckoutPath ? children : <RiskSessionManagerProvider>
          <RiskifiedBeacon />
          {children}
        </RiskSessionManagerProvider>}
    </BuyWidgetStateProvider>;
};
type BuyWidgetProviderProps = I18nProviderProps & {
  initParamsFromServer: InitParamsType | undefined;
  internalInitParamsFromServer: InternalInitParamsType | undefined;
};
export const BuyWidgetProvider: React.FC<BuyWidgetProviderProps> = ({
  children,
  initParamsFromServer,
  internalInitParamsFromServer,
  locale,
  messages
}) => {
  return <WidgetProvider widgetBackgroundVariant="solid" WidgetContentProvider={BuyWidgetContentProvider} initParamsFromServer={initParamsFromServer} internalInitParamsFromServer={internalInitParamsFromServer} locale={locale} messages={messages}>
      <OnrampNetworksProvider>{children}</OnrampNetworksProvider>
    </WidgetProvider>;
};

require("./providers.linaria.module.css!=!../../../node_modules/linaria/lib/outputCssLoader.js?cacheProvider=!./providers.tsx");